/**
 * InvestmentTab - Investment Items for a specific building
 *
 * Shows investment items (CAPEX ideas) filtered by the building.
 * Allows creating, editing, and viewing investment items for this building.
 */

import { memo, useState, useCallback, useMemo, useEffect } from "react";
import { Plus, Wallet } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  InvestmentItem,
  InvestmentItemStatus,
  INVESTMENT_STATUS_LABELS,
} from "screens/investment/types/investment";
import { BuildingDTO } from "shared/types/building";

// Format currency helper
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// Status badge colors
const getStatusColor = (status: InvestmentItemStatus) => {
  switch (status) {
    case InvestmentItemStatus.ONTWERP:
      return "bg-gray-100 text-gray-700";
    case InvestmentItemStatus.IN_BEOORDELING:
      return "bg-blue-100 text-blue-700";
    case InvestmentItemStatus.GOEDGEKEURD:
      return "bg-green-100 text-green-700";
    case InvestmentItemStatus.AFGEWEZEN:
      return "bg-red-100 text-red-700";
    case InvestmentItemStatus.IN_PROJECT:
      return "bg-moss-primary/10 text-moss-primary";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

interface InvestmentTabProps {
  building: BuildingDTO;
}

const InvestmentTabComponent = ({ building }: InvestmentTabProps) => {
  const { session } = useSession();
  const [items, setItems] = useState<InvestmentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch investment items for this building
  const fetchItems = useCallback(async () => {
    if (!session || !building?.id) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/investment/items?buildingId=${building.id}&limit=100`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setItems(data.data || []);
      } else {
        throw new Error("Failed to fetch investment items");
      }
    } catch (error) {
      console.error("Error fetching investment items:", error);
      toast.error("Fout bij ophalen investeringslijnen");
    } finally {
      setIsLoading(false);
    }
  }, [session, building?.id]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const stats = {
      totalCost: 0,
      allocatedCost: 0,
      byStatus: {} as Record<InvestmentItemStatus, number>,
    };

    Object.values(InvestmentItemStatus).forEach((status) => {
      stats.byStatus[status] = 0;
    });

    items.forEach((item) => {
      stats.totalCost += Number(item.estimatedCostExclVat) || 0;
      stats.allocatedCost += Number(item.allocatedBudget) || 0;
      if (stats.byStatus[item.status] !== undefined) {
        stats.byStatus[item.status]++;
      }
    });

    return stats;
  }, [items]);

  const handleNavigateToInvestment = () => {
    window.location.href = `/investment?buildingId=${building.id}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Laden...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Wallet size={24} weight="duotone" className="text-moss-primary" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Investeringslijnen
            </h2>
            <p className="text-sm text-gray-500">
              {items.length} items - Totaal: {formatCurrency(statistics.totalCost)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleNavigateToInvestment}
          >
            Naar overzicht
          </Button>
          <Button
            size="sm"
            onClick={() =>
              (window.location.href = `/investment?buildingId=${building.id}&create=true`)
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Nieuw item
          </Button>
        </div>
      </div>

      {/* Statistics */}
      {items.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(statistics.totalCost)}
            </div>
            <div className="text-xs text-gray-500 uppercase">Totaal budget</div>
          </div>
          <div className="rounded-lg bg-moss-primary/10 p-4">
            <div className="text-2xl font-bold text-moss-primary">
              {formatCurrency(statistics.allocatedCost)}
            </div>
            <div className="text-xs text-gray-500 uppercase">In projecten</div>
          </div>
          <div className="rounded-lg bg-green-50 p-4">
            <div className="text-2xl font-bold text-green-700">
              {statistics.byStatus[InvestmentItemStatus.GOEDGEKEURD] || 0}
            </div>
            <div className="text-xs text-gray-500 uppercase">Goedgekeurd</div>
          </div>
          <div className="rounded-lg bg-blue-50 p-4">
            <div className="text-2xl font-bold text-blue-700">
              {statistics.byStatus[InvestmentItemStatus.IN_BEOORDELING] || 0}
            </div>
            <div className="text-xs text-gray-500 uppercase">In beoordeling</div>
          </div>
        </div>
      )}

      {/* Items list */}
      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
          <Wallet className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-gray-500">
            Geen investeringslijnen voor dit gebouw
          </p>
          <Button
            className="mt-4"
            onClick={() =>
              (window.location.href = `/investment?buildingId=${building.id}&create=true`)
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Eerste item toevoegen
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Titel
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                  Budget
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                  In project
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => (window.location.href = `/investment?itemId=${item.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {item.title}
                      </span>
                      {item.status === InvestmentItemStatus.IN_PROJECT && item.renovationProject?.id && (
                        <span className="text-xs text-moss-primary">
                          RENO-{item.renovationProject.id}
                        </span>
                      )}
                    </div>
                    {item.label && (
                      <div className="text-xs text-gray-500">{item.label.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(item.status)}`}
                    >
                      {INVESTMENT_STATUS_LABELS[item.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {formatCurrency(item.estimatedCostExclVat || 0)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {item.allocatedBudget > 0 ? (
                      <span className="font-medium text-moss-primary">
                        {formatCurrency(item.allocatedBudget)}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const InvestmentTab = memo(InvestmentTabComponent);
export default InvestmentTab;
